export const CHECKOUT_URL = 'https://orthomap-en.impultienda.ar/checkout/';

export const images = {
  product: 'https://cdn.magicpatterns.com/uploads/jPPhH4uneRYgk5qUpz2HJH/ORTHOMAP-EN.png',
  doctor: 'https://cdn.magicpatterns.com/uploads/ggkxZdrmsWxeTC5yUeyjWh/DR._NATHAN_BROOKS_-_ORTHO.png',
};

export const planningQuestions = [
  'What is the actual problem?',
  'What are the treatment objectives?',
  'What needs to happen first?',
  'What should not be moved yet?',
  'Where will anchorage come from?',
  'What are the limitations?',
  'How will you know when each phase is complete?',
  'What could derail the treatment?',
];

export const sequenceSteps = ['Assess', 'Define', 'Prioritize', 'Map', 'Sequence', 'Review'];

export const beforeItems = [
  'You look at the patient and immediately start thinking about mechanics.',
  'You jump between diagnosis, appliances, anchorage and tooth movement.',
  'You have important information scattered across notes, photographs and radiographs.',
  'You know several possible approaches but struggle to organize them into one sequence.',
  'You sometimes realize a problem only after treatment has already started.',
  'Every new case feels like starting from a blank page.',
];

export const afterItems = [
  'You begin with a structured case assessment.',
  'You define the treatment objectives before choosing mechanics.',
  'You separate problems from objectives and objectives from movements.',
  'You map anchorage requirements before initiating movement.',
  'You organize the treatment into logical phases.',
  'You have a visual framework you can reuse for every case.',
];

export const painPoints = [
  'You have a case in front of you and know there are several things to consider, but you’re not sure what should come first.',
  'You spend too much time reviewing notes, textbooks, screenshots and previous cases before you can organize a treatment plan.',
  'You understand individual orthodontic concepts, but connecting them into one coherent treatment sequence is still difficult.',
  'You sometimes think about brackets, wires or mechanics before fully defining the treatment objectives.',
  'You worry that an overlooked anchorage requirement or sequencing problem could create unnecessary complications later.',
  'Every complex case requires you to mentally rebuild your planning process from scratch.',
];

export const disciplines = ['Diagnosis', 'Biomechanics', 'Anchorage', 'Tooth movement', 'Finishing'];

export type Volume = {
  numeral: string;
  title: string;
  lead: string[];
  flow?: string[];
  bullets?: string[];
  closing?: string;
};

export const volumes: Volume[] = [
  {
    numeral: 'I',
    title: 'The 15-Minute Orthodontic Case Assessment',
    lead: [
      'Before deciding what to move, you need to understand what you’re looking at.',
      'I’ll walk you through a structured assessment process designed to help you organize the most important findings before building your treatment plan.',
    ],
    closing: 'You’ll learn how to move through the case systematically instead of jumping immediately into mechanics.',
  },
  {
    numeral: 'II',
    title: 'Turn Clinical Findings Into Clear Treatment Objectives',
    lead: ['A finding isn’t automatically a treatment objective. This volume helps you separate:'],
    flow: ['What you see', 'What it means', 'What you want to change', 'Why you want to change it'],
    closing:
      'You’ll build a clear problem list and convert it into prioritized treatment objectives. This is where the case starts becoming a map instead of a collection of observations.',
  },
  {
    numeral: 'III',
    title: 'Build Your Anchorage & Movement Map Before You Start',
    lead: [
      'One of the biggest planning mistakes is deciding how to move teeth before thinking about what must remain stable. Inside this volume, you’ll learn how to map:',
    ],
    bullets: [
      'Anchorage requirements',
      'Intended movements',
      'Movement priorities',
      'Areas requiring control',
      'Dependencies between movements',
      'Potential conflicts',
    ],
    closing: 'Know what needs to move — and what needs to stay where it is — before you begin.',
  },
  {
    numeral: 'IV',
    title: 'Put Every Movement in the Right Order',
    lead: [
      'A treatment plan isn’t just a list of things you want to accomplish. Sequence matters. This volume gives you a visual framework for organizing treatment into logical phases. You’ll learn how to identify:',
    ],
    bullets: [
      'What needs to happen first',
      'What can happen simultaneously',
      'What should wait',
      'What depends on another movement',
      'When to reassess',
      'When a phase is complete',
    ],
    closing: 'Instead of asking “What should I do next?” — you’ll have a map that shows you where you are in the treatment sequence.',
  },
  {
    numeral: 'V',
    title: 'Prevent Planning Errors Before They Become Treatment Problems',
    lead: [
      'Not every treatment problem begins with poor execution. Some begin with the original plan. This volume gives you a pre-treatment review system to identify potential problems involving:',
    ],
    bullets: [
      'Anchorage',
      'Sequencing',
      'Uncontrolled movements',
      'Conflicting objectives',
      'Incomplete diagnosis',
      'Missing reassessment points',
      'Finishing requirements',
    ],
    closing: 'You’ll learn how to perform a Treatment Map Audit before committing to the sequence.',
  },
  {
    numeral: 'VI',
    title: 'Turn Your Treatment Plan Into a Complete Case Roadmap',
    lead: ['Now you’ll put everything together. You’ll learn how to create a complete one-page treatment roadmap containing:'],
    flow: ['Diagnosis', 'Objectives', 'Priorities', 'Anchorage', 'Movements', 'Sequence', 'Reassessment', 'Finishing'],
    closing: 'This becomes the document you can return to throughout the case. No more trying to remember your entire strategy. Your strategy is on the map.',
  },
];

export const tools = [
  {
    title: '30+ Treatment Planning Protocols',
    text: 'Structured protocols covering the key planning decisions you’ll encounter throughout the treatment process.',
  },
  {
    title: 'Professional Case Checklists',
    text: 'Reusable checklists designed to help you review each stage of your case without starting from zero.',
  },
  {
    title: 'Treatment Decision Algorithms',
    text: 'Visual decision pathways that help you organize your thinking before choosing a treatment approach.',
  },
  {
    title: 'Case Mapping Matrices',
    text: 'Simple matrices for organizing Problem → Objective → Movement → Anchorage → Sequence.',
  },
  {
    title: 'Visual Treatment Maps',
    text: 'Planning sheets you can print, duplicate and use as your case develops.',
  },
];

export const bonuses = [
  {
    number: 1,
    title: '30 Orthodontic Case Decision Cards™',
    value: 37,
    image: 'https://cdn.magicpatterns.com/uploads/oi9MP9cEr7J3ko87AK9biP/BONUS_1_ORTHOMAP-EN.png',
    text: [
      'Short, practical case prompts designed to make you stop and think before choosing a treatment direction.',
      'Each card presents a planning situation followed by the key questions you should ask yourself. Use them to practice your decision-making between patients.',
    ],
  },
  {
    number: 2,
    title: 'The 10-Minute Case Mapping Protocol™',
    value: 27,
    image: 'https://cdn.magicpatterns.com/uploads/r9LhRi38EmRh23qWabVSik/BONUS_2_ORTHOMAP-EN.png',
    text: [
      'A rapid planning worksheet that takes you from Patient Findings → Problem List → Objectives → Priorities → Treatment Map.',
      'Designed to help you create a first structured case map in approximately 10 minutes.',
    ],
  },
  {
    number: 3,
    title: 'The Anchorage Audit™',
    value: 29,
    image: 'https://cdn.magicpatterns.com/uploads/18qKpQAnkzhcuNf8BS8Cs7/BONUS_3_ORTHOMAP-EN.png',
    text: [
      'A dedicated checklist for reviewing anchorage requirements before initiating planned movements.',
      'Use it as a final checkpoint before moving from planning into execution.',
    ],
  },
  {
    number: 4,
    title: 'The Orthodontic Planning Mistake Library',
    value: 27,
    image: 'https://cdn.magicpatterns.com/uploads/gomoRiTAw8cC5EwCQQ2u2k/BONUS_4_ORTHOMAP-EN.png',
    text: [
      'A visual collection of common planning mistakes involving diagnosis, sequencing, anchorage, movement priorities and finishing.',
      'The purpose isn’t simply to show you what went wrong. It’s to help you ask: “Could I have identified this before treatment started?”',
    ],
  },
  {
    number: 5,
    title: 'The One-Page Case Presentation Template™',
    value: 27,
    image: 'https://cdn.magicpatterns.com/uploads/dmGf5k1pph8f7HgVciVezE/BONUS_5_ORTHOMAP-EN.png',
    text: [
      'A professional template for organizing a case into one clear visual summary: Diagnosis, Problems, Objectives, Priorities, Anchorage, Sequence, Reassessment, Finishing.',
      'Perfect for reviewing your own cases or communicating the treatment strategy with your team.',
    ],
  },
];

export const offerIncludes = [
  'One-time payment',
  'Lifetime access',
  'No monthly fees',
  'Immediate digital access',
  '7-Day Guarantee',
];

export const guaranteeItems = [
  'Explore all 6 volumes',
  'Review the treatment protocols',
  'Use the case maps',
  'Test the checklists',
  'Work through the planning worksheets',
  'Apply the system to your own educational case planning',
];

export const testimonials = [
  {
    headline: 'I finally stopped starting every case from a blank page.',
    body: 'The biggest difference for me was the sequence. I already knew the individual concepts, but having everything organized into one treatment map made the planning process much clearer.',
    name: 'Dr. Michael R.',
    country: 'USA',
    photo: 'https://cdn.magicpatterns.com/patterns/generated-images/955951b0-7f37-4164-aba9-0d6942889e31.jpg',
  },
  {
    headline: 'The 15-minute assessment changed how I approach new cases.',
    body: 'Instead of immediately thinking about mechanics, I now start by organizing the problems and objectives. It gives me a much clearer starting point.',
    name: 'Dr. Sarah M.',
    country: 'Canada',
    photo: 'https://cdn.magicpatterns.com/patterns/generated-images/fede6e41-245c-4f19-a920-1a0982af7d5d.jpg',
  },
  {
    headline: 'The anchorage map is incredibly practical.',
    body: 'I particularly liked having the anchorage requirements written down before thinking about movement. It’s a simple change, but it completely changes the way I review a case.',
    name: 'Dr. Daniel P.',
    country: 'Australia',
    photo: 'https://cdn.magicpatterns.com/patterns/generated-images/8543a6e1-ccfe-4e56-a00f-18ae0d25e253.jpg',
  },
  {
    headline: 'It feels like a clinical planning system, not another PDF.',
    body: 'The visual maps and checklists are what make it useful. I can actually use them while reviewing a case instead of just reading another textbook.',
    name: 'Dr. James K.',
    country: 'United Kingdom',
    photo: 'https://cdn.magicpatterns.com/patterns/generated-images/0612bd5a-88f6-484d-af95-fd8a1aad1804.jpg',
  },
  {
    headline: 'The one-page roadmap became my favorite tool.',
    body: 'Being able to see the diagnosis, objectives, anchorage, sequence and finishing requirements together makes complex cases much easier to review.',
    name: 'Dr. Emily T.',
    country: 'USA',
    photo: 'https://cdn.magicpatterns.com/patterns/generated-images/36dad2fe-940c-434d-aa4c-8dd4a5d9c12b.jpg',
  },
];

export const aboutChain = [
  'Diagnosis',
  'Treatment objectives',
  'Anchorage',
  'Biomechanics',
  'Movement',
  'Sequencing',
  'Reassessment',
  'Finishing',
];

export const aboutApproach = [
  'First understand it.',
  'Then define the objective.',
  'Then identify the constraints.',
  'Then decide what needs to move.',
  'Then decide what needs to remain stable.',
  'Then build the sequence.',
  'Then review the map before treatment begins.',
];

export const faqs = [
  {
    q: 'How will I receive the material?',
    a: 'Immediately after your payment, you’ll receive access instructions by email. The 6 volumes and 5 bonuses are delivered digitally.',
  },
  {
    q: 'Is Ortho Treatment Map™ a course?',
    a: 'No. It is a digital treatment-planning system made up of structured volumes, maps, protocols, checklists, decision tools and worksheets. The goal is to give you practical planning resources rather than another long video course.',
  },
  {
    q: 'Do I need to be an orthodontic specialist?',
    a: 'The system is designed for dental professionals who already have foundational knowledge of orthodontic diagnosis and treatment. It is not intended to replace formal orthodontic education, specialist training or patient-specific clinical judgment.',
  },
  {
    q: 'Can I use the maps more than once?',
    a: 'Yes. That’s one of the main reasons I created the system. You can print the worksheets, duplicate them digitally or use them as templates for future case planning.',
  },
  {
    q: 'Is this a one-time payment?',
    a: 'Yes. Your payment is a single $27 USD purchase. There are no monthly subscription fees.',
  },
  {
    q: 'Do I get lifetime access?',
    a: 'Yes. Once you purchase the system, you can access the materials for as long as the product remains available.',
  },
  {
    q: 'Can I use it for complex cases?',
    a: 'The system is designed to help you organize and review complex treatment-planning considerations. However, every patient is different, and the appropriate diagnosis and treatment approach must always be determined using professional clinical judgment and the patient’s individual circumstances.',
  },
  {
    q: 'What if I don’t like it?',
    a: 'You have a 7-day guarantee. Explore the system, review the materials and decide whether it provides value for your workflow. If it isn’t right for you, contact us within 7 days for a full refund.',
  },
];

export const finalChain = ['Patient', 'Diagnosis', 'Objectives', 'Anchorage', 'Movements', 'Sequence', 'Reassessment', 'Finishing'];

export const recentBuyers = [
  { name: 'María González', country: 'Mexico' },
  { name: 'Carlos Ramírez', country: 'Colombia' },
  { name: 'Lucía Fernández', country: 'Argentina' },
  { name: 'Javier Morales', country: 'Spain' },
  { name: 'Valentina Rojas', country: 'Chile' },
  { name: 'Diego Castillo', country: 'Peru' },
  { name: 'Camila Herrera', country: 'Ecuador' },
  { name: 'Andrés Medina', country: 'Venezuela' },
  { name: 'Sofía Vargas', country: 'Costa Rica' },
  { name: 'Mateo Jiménez', country: 'Uruguay' },
  { name: 'Daniela Torres', country: 'Dominican Republic' },
  { name: 'Alejandro Ruiz', country: 'Guatemala' },
  { name: 'Isabella Navarro', country: 'Panama' },
  { name: 'Fernando Ortiz', country: 'Bolivia' },
  { name: 'Gabriela Silva', country: 'Paraguay' },
];
